// src/Components/Marketplace/services/marketplaceApi.js
// Customer-facing marketplace API client (browse services/packages, book, cancel, review).
// Mirrors the auth pattern used across the rest of this app: Bearer token read straight
// from localStorage("token") — the same key EmailLogin.jsx / saveToken() write to.
import axios from "axios";
import baseURL from "../../../Services/Config";

const marketplaceAxios = axios.create({
  baseURL,
  timeout: 20000,
});

marketplaceAxios.interceptors.request.use((config) => {
  const token =
    localStorage.getItem("token") || localStorage.getItem("authToken");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// ── Browse (public) ──────────────────────────────────────────────
export const getServices = async (params = {}) => {
  const res = await marketplaceAxios.get("/marketplace/services", { params });
  return res.data;
};

export const getServiceById = async (id) => {
  const res = await marketplaceAxios.get(`/marketplace/services/${id}`);
  return res.data;
};

export const getPackages = async (params = {}) => {
  const res = await marketplaceAxios.get("/marketplace/packages", { params });
  return res.data;
};

export const getPackageById = async (id) => {
  const res = await marketplaceAxios.get(`/marketplace/packages/${id}`);
  return res.data;
};

export const getPartnerReviews = async (partnerId) => {
  const res = await marketplaceAxios.get(
    `/marketplace/partners/${partnerId}/reviews`
  );
  return res.data;
};

// ── Booking / purchase (auth required) ───────────────────────────
export const bookService = async (serviceId, payload) => {
  const res = await marketplaceAxios.post(
    `/marketplace/services/${serviceId}/book`,
    payload
  );
  return res.data;
};

export const purchasePackage = async (packageId, payload) => {
  const res = await marketplaceAxios.post(
    `/marketplace/packages/${packageId}/purchase`,
    payload
  );
  return res.data;
};

export const verifyMarketplacePayment = async (payload) => {
  const res = await marketplaceAxios.post("/marketplace-payment/verify", payload);
  return res.data;
};

// ── My trips / cancellation / review (auth required) ─────────────
export const getMyMarketplaceTrips = async () => {
  const res = await marketplaceAxios.get("/travel-request/my-marketplace-trips");
  return res.data;
};

export const getCancellationPreview = async (bookingId) => {
  const res = await marketplaceAxios.get(
    `/travel-request/bookings/${bookingId}/cancellation-preview`
  );
  return res.data;
};

export const cancelMarketplaceBooking = async (bookingId, reason) => {
  const res = await marketplaceAxios.post(
    `/travel-request/bookings/${bookingId}/cancel`,
    { reason }
  );
  return res.data;
};

export const submitBookingReview = async (bookingId, { rating, text }) => {
  const res = await marketplaceAxios.post(
    `/travel-request/bookings/${bookingId}/review`,
    { rating, text }
  );
  return res.data;
};

export const getApiErrorMessage = (err, fallback = "Something went wrong. Please try again.") =>
  err?.response?.data?.message ||
  err?.response?.data?.error ||
  err?.message ||
  fallback;

export default marketplaceAxios;
